'use client'

import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { Box, Button, Checkbox, Group, NumberInput, Radio, Stack, Text, Textarea, Title } from '@mantine/core'
import { notifySuccess } from '@phoenix-web/react-shared/client-components'
import { redactPropsWithClassName } from '@phoenix-web/utils/general'
import { usePostPropertyDisclosureForm } from '@phoenix-web/ux-gql/hooks'

export interface PropertyDisclosureFormProps {
  transactionId: number
  propertyAddress?: string
  redirectPath: string
  // App-owned side effect after submit (e.g. Heap tracking). Omitted by consumers that
  // shouldn't fire analytics. Invoked from a client wrapper so the callback never crosses
  // the RSC boundary.
  onSubmitSuccess?: (payload: { transactionId: number }) => void
}

const initialFormData = {
  propertyIssues: [] as string[],
  issueDetails: '',
  ageRestricted: '',
  condominium: '',
  hasHoa: '',
  hoaDues: '',
  additionalLand: '',
  specialAssessments: '',
  assessmentDetails: '',
  tenantOccupancy: '',
  tenantOccupancyDetails: '',
}

export default function PropertyDisclosureForm({
  transactionId,
  propertyAddress,
  redirectPath,
  onSubmitSuccess,
}: PropertyDisclosureFormProps) {
  const router = useRouter()
  const [postPropertyDisclosureForm, { loading, error }] = usePostPropertyDisclosureForm()
  const [formData, setFormData] = useState(initialFormData)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [propertyIssuesError, setPropertyIssuesError] = useState<string | null>(null)

  useEffect(() => {
    if (isSubmitted) router.replace(redirectPath)
  }, [isSubmitted, redirectPath, router])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate that at least one checkbox is selected
    if (formData.propertyIssues.length === 0) {
      setPropertyIssuesError('Please select at least one option')
      return
    }

    setPropertyIssuesError(null)
    await postPropertyDisclosureForm({
      variables: { input: { transaction_id: transactionId, ...formData } },
    })

    setFormData(initialFormData)
    try {
      onSubmitSuccess?.({ transactionId })
    } catch {
      // Consumer callbacks (analytics) must not block the success/redirect path.
    }
    notifySuccess({
      message: 'Your Property Disclosure has been submitted.',
      autoClose: 10000,
    })
    setIsSubmitted(true)
  }

  const propertyIssueOptions = [
    'Roof',
    'Major systems (HVAC, water heater, septic, etc.)',
    'Water or moisture damage',
    'Unpermitted additions or renovations',
    'Wood-destroying organisms (e.g., termites)',
    'Foundation',
    'Storm or natural disaster damage',
    'Other',
    'No Issues',
  ]

  const handleIssueChange = (value: string, checked: boolean) => {
    // Clear error when user selects a checkbox
    if (checked) {
      setPropertyIssuesError(null)
    }

    if (value === 'No Issues') {
      setFormData({ ...formData, propertyIssues: checked ? ['No Issues'] : [] })
    } else {
      const newIssues = checked
        ? [...formData.propertyIssues.filter((issue) => issue !== 'No Issues'), value]
        : formData.propertyIssues.filter((issue) => issue !== value)
      setFormData({ ...formData, propertyIssues: newIssues })
    }
  }

  const hasIssuesSelected = formData.propertyIssues.length > 0 && !formData.propertyIssues.includes('No Issues')
  const showHoaDues = formData.hasHoa === 'yes'
  const showAssessmentDetails = formData.specialAssessments === 'yes'
  const showTenantDetails = formData.tenantOccupancy === 'yes'

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="lg">
        {/* Title and Address */}
        <Box>
          <Text fw="bold">Property Disclosure Form</Text>
          <Title c="brand-blue.9" order={1} {...redactPropsWithClassName}>
            {propertyAddress}
          </Title>
        </Box>

        <Text ta="left" fz="md" mb="xl">
          Please provide details about your departing property&apos;s condition, any known issues, and community
          restrictions. This information is required as part of the pre-approval process and must be completed before we
          can proceed.
        </Text>

        {/* Property Condition Section */}
        <Box>
          <Title order={3} mb="sm">
            Property Condition
          </Title>
          <Text fw={600} size="md">
            Does your property currently have any issues or repairs related to the following?*
          </Text>
          <Text size="md" mb="md" c="gray">
            (Select all that apply)
          </Text>
          <Stack gap="sm">
            {propertyIssueOptions.map((option) => (
              <Checkbox
                key={option}
                label={option}
                checked={formData.propertyIssues.includes(option)}
                onChange={(event) => handleIssueChange(option, event.currentTarget.checked)}
              />
            ))}
          </Stack>

          {propertyIssuesError && (
            <Text c="red" size="sm" mt="xs">
              {propertyIssuesError}
            </Text>
          )}

          {hasIssuesSelected && (
            <Box mt="md">
              <Text fw={600} size="md" mb="sm">
                Please provide details for all areas with issues. Enter details for each selected item.
              </Text>
              <Textarea
                value={formData.issueDetails}
                onChange={(e) => setFormData({ ...formData, issueDetails: e.target.value })}
                placeholder="Provide details about any issues..."
                minRows={3}
                size="md"
                required
              />
            </Box>
          )}

          <Box mt="md">
            <Text fw={600} size="md" mb="sm">
              Is any part of the departing property currently rented or available as a rental?*
            </Text>
            <Radio.Group
              value={formData.tenantOccupancy}
              onChange={(value) =>
                setFormData({
                  ...formData,
                  tenantOccupancy: value,
                  tenantOccupancyDetails: value === 'yes' ? formData.tenantOccupancyDetails : '',
                })
              }
              name="tenantOccupancy"
            >
              <Group mt="xs">
                <Radio value="yes" label="Yes" required />
                <Radio value="no" label="No" required />
              </Group>
            </Radio.Group>
          </Box>

          {showTenantDetails && (
            <Box mt="md">
              <Text fw={600} size="md" mb="sm">
                Please provide more details about the rental status.
              </Text>
              <Textarea
                value={formData.tenantOccupancyDetails}
                onChange={(e) => setFormData({ ...formData, tenantOccupancyDetails: e.target.value })}
                placeholder="Provide details about the rental status..."
                minRows={3}
                size="md"
                required
              />
            </Box>
          )}
        </Box>

        {/* Community Details Section */}
        <Box>
          <Title order={3} mb="sm">
            Community Details
          </Title>

          <Box mb="md">
            <Text fw={600} size="md" mb="sm">
              Is your home located in an age-restricted community?*
            </Text>
            <Radio.Group
              value={formData.ageRestricted}
              onChange={(value) => setFormData({ ...formData, ageRestricted: value })}
              name="ageRestricted"
            >
              <Group mt="xs">
                <Radio value="yes" label="Yes" required />
                <Radio value="no" label="No" required />
              </Group>
            </Radio.Group>
          </Box>

          <Box mb="md">
            <Text fw={600} size="md" mb="sm">
              Is your home part of a condominium community?*
            </Text>
            <Radio.Group
              value={formData.condominium}
              onChange={(value) => setFormData({ ...formData, condominium: value })}
              name="condominium"
            >
              <Group mt="xs">
                <Radio value="yes" label="Yes" required />
                <Radio value="no" label="No" required />
              </Group>
            </Radio.Group>
          </Box>

          <Box mb="md">
            <Text fw={600} size="md" mb="sm">
              Do you have an HOA?*
            </Text>
            <Radio.Group
              value={formData.hasHoa}
              onChange={(value) => setFormData({ ...formData, hasHoa: value })}
              name="hasHoa"
            >
              <Group mt="xs">
                <Radio value="yes" label="Yes" required />
                <Radio value="no" label="No" required />
              </Group>
            </Radio.Group>
          </Box>

          {showHoaDues && (
            <Box>
              <Text fw={600} size="md" mb="sm">
                What are your annual HOA dues?*
              </Text>
              <NumberInput
                value={formData.hoaDues}
                onChange={(value) => setFormData({ ...formData, hoaDues: value?.toString() || '' })}
                placeholder="$"
                prefix="$"
                min={0}
                max={99999}
                decimalScale={2}
                thousandSeparator=","
                required
                allowNegative={false}
                w={120}
                maxLength={7}
                size="md"
              />
            </Box>
          )}
        </Box>

        {/* Land Use, Taxes & Easements Section */}
        <Box>
          <Title order={3} mb="sm">
            Land Use, Taxes & Easements
          </Title>

          <Box mb="md">
            <Text fw={600} size="md" mb="sm">
              Do you plan to sell any additional plots or parcels of land with your property?
            </Text>
            <Radio.Group
              value={formData.additionalLand}
              onChange={(value) => setFormData({ ...formData, additionalLand: value })}
              name="additionalLand"
            >
              <Group mt="xs">
                <Radio value="yes" label="Yes" required />
                <Radio value="no" label="No" required />
              </Group>
            </Radio.Group>
          </Box>

          <Box mb="md">
            <Text fw={600} size="md" mb="sm">
              Does your property have any special tax assessments or easements?*
            </Text>
            <Radio.Group
              value={formData.specialAssessments}
              onChange={(value) => setFormData({ ...formData, specialAssessments: value })}
              name="specialAssessments"
            >
              <Group mt="xs">
                <Radio value="yes" label="Yes" required />
                <Radio value="no" label="No" required />
              </Group>
            </Radio.Group>
          </Box>

          {showAssessmentDetails && (
            <Box>
              <Text fw={600} size="md" mb="sm">
                Please provide details about special assessments or easements.
              </Text>
              <Textarea
                value={formData.assessmentDetails}
                onChange={(e) => setFormData({ ...formData, assessmentDetails: e.target.value })}
                placeholder="Provide details about special assessments or easements..."
                minRows={3}
                required
                size="md"
              />
            </Box>
          )}
        </Box>

        {error && <Text c="red">{error.message}</Text>}

        {/* Action Buttons */}
        <Group justify="flex-end" mt="lg">
          <Button variant="outline" color="blue" onClick={() => router.replace(redirectPath)}>
            Cancel
          </Button>
          <Button type="submit" variant="filled" disabled={loading}>
            Submit
          </Button>
        </Group>
      </Stack>
    </form>
  )
}
